import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import DotboxLogo from '@/components/common/DotboxLogo';
import ContactSupportModal from '@/components/common/ContactSupportModal';
import BookDemoModal from '@/components/landing/BookDemoModal';
import LandingNav from '@/components/landing/LandingNav';
import { PRICING_TIERS, formatCap, formatSeats } from '@/constants/pricing';
import { SUITE_MODULES, DECISION_LAB_MODULE } from '@/constants/landing';
import {
  Zap,
  BarChart3,
  Shield,
  ArrowRight,
  Check,
  Database,
  HelpCircle,
  Calendar,
  Sparkles,
  TrendingUp,
  Network,
  FolderKanban,
  Users,
  Globe,
  Bookmark,
  FileText,
  Bot,
  Quote,
} from 'lucide-react';

const LandingPage = () => {
  const [showSupport, setShowSupport] = useState(false);
  const [showBookDemo, setShowBookDemo] = useState(false);
  const location = useLocation();

  // iter126 — external URLs / email links / marketing collateral may point at
  // `/book-a-demo` (or `/?openBookDemo=1`). Both paths are valid entry points
  // to the demo booking flow, but the modal itself lives on LandingPage —
  // there's no dedicated route/component. Auto-open the modal when either
  // signal is present so those inbound links don't blank-page.
  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    if (location.pathname === "/book-a-demo" || params.get("openBookDemo") === "1") {
      setShowBookDemo(true);
    }
  }, [location.pathname, location.search]);

  // iter112 — anchor scroll for links like `/#pricing` and `/#suite-signals`.
  // The Product ▾ dropdown in LandingNav (and any anchor from /contact) sets
  // window.location.hash on route change; React Router doesn't auto-scroll
  // to fragment IDs, so we do it here. A short setTimeout lets the sections
  // render (they're below the sticky nav) before scrollIntoView fires.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => clearTimeout(t);
  }, [location]);
  const features = [
    {
      icon: <Database className="h-7 w-7 text-amber-400" />,
      title: 'Multi-Source Data Aggregation',
      description: 'Unify APIs, RSS feeds, social streams, and webhooks into one continuously updated intelligence layer.'
    },
    {
      icon: <Zap className="h-7 w-7 text-amber-400" />,
      title: 'Real-Time Signal Streaming',
      description: 'Ingest, filter, and act on signals the moment they happen — ultra-low-latency by design.'
    },
    {
      icon: <BarChart3 className="h-7 w-7 text-amber-400" />,
      title: 'AI-Powered Analytics',
      description: 'Summaries, sentiment, clustering, translation, and highlights — engineered for decision-grade output.'
    },
    {
      icon: <Shield className="h-7 w-7 text-amber-400" />,
      title: 'Enterprise-Grade Security',
      description: 'Encryption at rest and in transit, RBAC, audit trails, and 2FA — governance is built in, not bolted on.'
    },
    {
      icon: <Sparkles className="h-7 w-7 text-amber-400" />,
      title: 'Analysis Agents Workspace',
      description: '24 specialist agents across 8 modules — competitor teardowns, buyer-intent scoring, macro thesis drafts — orchestrated in one workspace.'
    },
    {
      icon: <TrendingUp className="h-7 w-7 text-amber-400" />,
      title: 'Economist Live',
      description: 'Institutional-grade macro analyses streamed section-by-section, grounded in live FRED, BLS, and IMF data.'
    },
    {
      icon: <Network className="h-7 w-7 text-amber-400" />,
      title: 'Relationship Intelligence',
      description: 'A live connections graph of people, companies, and topics — see who overlaps, who moves markets, and who to talk to next.'
    },
    {
      icon: <FolderKanban className="h-7 w-7 text-amber-400" />,
      title: 'File Vault + RAG',
      description: 'Upload PDFs, decks, and CSVs. Vector-indexed and query-ready — chat with your own knowledge base in seconds.'
    }
  ];

  const suiteModules = SUITE_MODULES;

  const faqs = [
    {
      q: 'How does the free trial work?',
      a: 'Every paid tier starts with a full-featured 7-day trial. No credit card required. When the trial ends you can upgrade in a click or keep read-only access to what you already collected — nothing is deleted.'
    },
    {
      q: 'What data sources can I connect?',
      a: 'Anything with a URL, an RSS feed, an API, a webhook, or an OAuth-enabled social account (LinkedIn, X, Facebook, Instagram, Threads, YouTube). Uploaded PDFs, decks, and CSVs go into the File Vault and become searchable via RAG.'
    },
    {
      q: 'How does the AI actually work?',
      a: 'dotbox routes queries through best-in-class LLMs (Claude, GPT, Gemini) under a single unified credit ledger. Analysis Agents ground every answer in your live signals, connections, uploaded files, and Economist macro data — never a black-box model on its own.'
    },
    {
      q: 'Is my data secure?',
      a: 'Encryption at rest and in transit, JWT + 2FA authentication, per-endpoint API rate limiting, IP-based access rules, audit trails, and RBAC-controlled team seats. Every OAuth integration uses short-lived scoped tokens that you can revoke at any time from the Integrations panel.'
    },
    {
      q: 'Do you offer team plans?',
      a: 'Yes — starting on the Pro tier. Team seats share dashboards, connections, saved lists, and AI credit budgets. The Enterprise tier adds custom seat counts, priority support, and Volume pricing on an annual contract.'
    },
    {
      q: 'Can I export my data?',
      a: 'Yes — JSON and CSV export from every list, saved search, and analytics report. Scheduled digest exports over email are included on the Growth tier and above.'
    },
    {
      q: 'Do you offer SSO / SAML?',
      a: 'SSO (SAML 2.0 and OIDC) is included on the Enterprise tier alongside custom RBAC roles and dedicated security review. Contact us and we can turn it on for your workspace within one business day.'
    },
    {
      q: 'What if I need something you don\'t have?',
      a: 'Book a demo and tell us. dotbox is engineered for operators who need real answers, not off-the-shelf slideware — most requests ship inside two weeks.'
    }
  ];

  const steps = [
    {
      step: '01',
      title: 'Connect Your Sources',
      description: 'Plug in APIs, RSS feeds, and social channels with no-code connectors.'
    },
    {
      step: '02',
      title: 'Configure Your Signals',
      description: 'Filter, transform, and route streams with precision rules and AI-assisted queries.'
    },
    {
      step: '03',
      title: 'Decide With Confidence',
      description: 'Trigger alerts, build dashboards, and share insights — all in one operational view.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A1024] text-slate-200">
      <LandingNav onOpenBookDemo={() => setShowBookDemo(true)} />

      {/* Hero Section — constellation pattern */}
      <section className="relative overflow-hidden constellation-bg">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-32 z-10">
          <div className="max-w-4xl">
            <Badge className="mb-6 bg-red-600/15 text-red-400 border border-red-600/40 hover:bg-red-600/20 uppercase tracking-widest text-[11px] font-semibold px-3 py-1">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-red-500 mr-2 animate-pulse" />
              All-in-One Multi-Source Streaming
            </Badge>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-[1.05] tracking-tight">
              Where Data Dots Connect.
              <br />
              <span className="bg-gradient-to-r from-red-500 via-red-400 to-red-600 bg-clip-text text-transparent">
                Insight Emerges.
              </span>
            </h1>
            <p className="text-lg md:text-xl text-slate-300 mb-4 max-w-2xl leading-relaxed">
              The operational intelligence platform for leaders who refuse to guess.
            </p>
            <p className="text-base text-slate-400 mb-12 max-w-2xl leading-relaxed">
              Aggregate every signal — APIs, RSS, social, web. Filter with AI. Act with conviction.
              Engineered for decisions that carry consequence.
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Button
                asChild
                size="lg"
                className="bg-[#DC2626] hover:bg-[#B91C1C] text-white px-8 py-6 text-base shadow-xl shadow-red-900/40"
                data-testid="hero-get-started-button"
              >
                <Link to="/register" className="flex items-center">
                  Start Free Trial
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-[#2A3658] bg-transparent text-slate-200 hover:bg-[#131C3A] hover:text-white px-8 py-6 text-base"
                data-testid="hero-signin-button"
              >
                <Link to="/login">Sign In</Link>
              </Button>
              <Button
                onClick={() => setShowBookDemo(true)}
                variant="outline"
                size="lg"
                className="border-red-600/50 bg-transparent text-red-300 hover:bg-red-950/30 hover:text-red-200 px-8 py-6 text-base"
                data-testid="hero-book-demo-button"
              >
                <Calendar className="mr-2 h-5 w-5" />
                Book a Demo
              </Button>
              <div className="flex items-center text-sm text-slate-400 sm:ml-2">
                <Check className="h-4 w-4 text-emerald-400 mr-2" />
                7-day trial — no credit card required
              </div>
            </div>
          </div>

          {/* Stats strip */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 border-t border-[#1F2A4D] pt-10">
            {[
              { value: '6+', label: 'Native social integrations' },
              { value: 'Real-Time', label: 'Signal streaming' },
              { value: 'AI-Driven', label: 'Filtering & summarization' },
              { value: '100%', label: 'Operator-controlled' }
            ].map((s) => (
              <div key={s.label}>
                <div className="text-2xl md:text-3xl font-bold text-white">{s.value}</div>
                <div className="text-xs uppercase tracking-widest text-slate-400 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-[#0A1024] border-t border-[#1F2A4D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16 max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] text-red-400 font-semibold mb-3">
              Built for Operators
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 tracking-tight leading-tight">
              Everything you need to master your data.
            </h2>
            <p className="text-base md:text-lg text-slate-400 leading-relaxed">
              From streaming ingestion to AI-driven analysis, dotbox gives operators an
              uncompromising command surface — no black-box outputs, no guesswork.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {features.map((feature) => (
              <Card
                key={feature.title}
                className="border hover:border-red-600/40 transition-colors duration-300 group"
              >
                <CardContent className="p-8">
                  <div className="flex items-start gap-5">
                    <div className="flex-shrink-0 p-3 bg-red-600/10 border border-red-600/30 rounded-lg group-hover:bg-red-600/15 transition-colors">
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-sm text-slate-400 leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Doctrine strip — quiet declaration between Features and How-It-Works */}
      <section
        className="relative py-14 bg-[#0F1730] border-t border-[#1F2A4D] overflow-hidden"
        data-testid="landing-doctrine-strip"
      >
        <div className="absolute inset-0 pointer-events-none opacity-40">
          <div className="absolute inset-0 bg-gradient-to-r from-red-600/5 via-transparent to-red-600/5" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 mb-4">
            <span className="h-px w-10 bg-red-600/40" />
            <Quote className="h-4 w-4 text-red-400" />
            <span className="h-px w-10 bg-red-600/40" />
          </div>
          <p className="text-xl md:text-2xl text-slate-100 font-light leading-relaxed max-w-3xl mx-auto italic">
            &ldquo;Never guess when you can <span className="text-red-400 font-medium not-italic">calculate</span>.
            Never react when you can <span className="text-red-400 font-medium not-italic">anticipate</span>.
            Never decide alone when your data can <span className="text-red-400 font-medium not-italic">speak</span>.&rdquo;
          </p>
          <div className="text-[11px] uppercase tracking-[0.24em] text-slate-500 mt-4">
            — the dotbox.ai doctrine
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-[#0F1730] border-y border-[#1F2A4D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="text-xs uppercase tracking-[0.2em] text-red-400 font-semibold mb-3">
              How It Works
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 tracking-tight">
              From signal to decision in three moves.
            </h2>
            <p className="text-base md:text-lg text-slate-400">
              No multi-month rollouts. No black-box outputs. Just an operating surface engineered for clarity.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step) => (
              <div key={step.step} className="relative">
                <div className="text-5xl font-bold text-amber-400/40 mb-4 font-mono">
                  {step.step}
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  {step.title}
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Full Product Suite — iter111 additive deep-dive between How-It-Works and Built-For */}
      <section
        id="suite"
        className="py-24 bg-[#0A1024] border-t border-[#1F2A4D] scroll-mt-16"
        data-testid="landing-suite-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-14 max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] text-red-400 font-semibold mb-3">
              The Full Product Suite
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 tracking-tight leading-tight">
              One platform · every operational surface an intelligence leader needs.
            </h2>
            <p className="text-base md:text-lg text-slate-400 leading-relaxed">
              dotbox is deliberately deep. Below is the shipping product — not a roadmap, not a pitch.
              Every module ships with test coverage, keyboard shortcuts, and public API access.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {suiteModules.map((mod) => {
              const Icon = mod.icon;
              return (
                <Card
                  key={mod.slug}
                  id={`suite-${mod.slug}`}
                  className="border hover:border-red-600/40 transition-colors duration-300 group scroll-mt-16"
                  data-testid={`landing-suite-card-${mod.slug}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex-shrink-0 p-2 bg-red-600/10 border border-red-600/30 rounded-md group-hover:bg-red-600/15 transition-colors">
                        <Icon className="h-5 w-5 text-red-400" />
                      </div>
                      <h3 className="text-base font-semibold text-white">{mod.name}</h3>
                    </div>
                    <p className="text-sm text-slate-400 leading-relaxed mb-4">
                      {mod.blurb}
                    </p>
                    <ul className="space-y-1.5">
                      {mod.bullets.map((b) => (
                        <li key={`${mod.slug}-${b}`} className="flex items-start text-xs text-slate-300">
                          <Check className="h-3 w-3 text-emerald-400 mr-2 flex-shrink-0 mt-0.5" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}

            {/* iter147 — Decision Lab as a wide horizontal card underneath the 12
                cards (spans all 3 columns on lg+). Same border / hover / colors
                as the 12 above so the grid stays visually symmetric at 3×4.
                On mobile it collapses to the same stacked look as any other card. */}
            {(() => {
              const DlIcon = DECISION_LAB_MODULE.icon;
              return (
                <Card
                  key={DECISION_LAB_MODULE.slug}
                  id={`suite-${DECISION_LAB_MODULE.slug}`}
                  className="lg:col-span-3 border hover:border-red-600/40 transition-colors duration-300 group scroll-mt-16"
                  data-testid={`landing-suite-card-${DECISION_LAB_MODULE.slug}`}
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8">
                      <div className="flex items-center gap-3 lg:min-w-[220px]">
                        <div className="flex-shrink-0 p-2 bg-red-600/10 border border-red-600/30 rounded-md group-hover:bg-red-600/15 transition-colors">
                          <DlIcon className="h-5 w-5 text-red-400" />
                        </div>
                        <h3 className="text-base font-semibold text-white">
                          {DECISION_LAB_MODULE.name}
                        </h3>
                      </div>
                      <p className="text-sm text-slate-400 leading-relaxed flex-1">
                        {DECISION_LAB_MODULE.blurb}
                      </p>
                      <ul className="flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-x-6 gap-y-1.5 lg:min-w-[280px]">
                        {DECISION_LAB_MODULE.bullets.map((b) => (
                          <li key={`${DECISION_LAB_MODULE.slug}-${b}`} className="flex items-start text-xs text-slate-300">
                            <Check className="h-3 w-3 text-emerald-400 mr-2 flex-shrink-0 mt-0.5" />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}
          </div>
        </div>
      </section>

      {/* Built For Section — iter91 additive platform-wide use cases */}
      <section
        className="py-20 bg-[#0A1024] border-t border-[#1F2A4D]"
        data-testid="landing-built-for-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 max-w-3xl mx-auto">
            <div className="text-xs uppercase tracking-[0.2em] text-red-400 font-semibold mb-3">
              Built For
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 tracking-tight">
              Every intelligence workflow that matters.
            </h2>
            <p className="text-sm md:text-base text-slate-400">
              From competitive intelligence to due diligence, sales ops to compliance —
              dotbox powers the operational surfaces decision-makers rely on.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 max-w-6xl mx-auto">
            {[
              { label: 'Competitive Intelligence',   icon: Zap },
              { label: 'Due Diligence',              icon: Shield },
              { label: 'Sales Intelligence',         icon: BarChart3 },
              { label: 'CRM Intelligence',           icon: Database },
              { label: 'Cyber Intelligence',         icon: Shield },
              { label: 'Legal & Compliance',         icon: Check },
              { label: 'Government & Public Sector', icon: BarChart3 },
              { label: 'Mergers & Acquisitions',     icon: Zap },
              { label: 'Media & Reputation',         icon: BarChart3 },
              { label: 'Relationship Intelligence',  icon: Database },
              { label: 'Macro Research',             icon: Globe },
              { label: 'AI Agent Operations',        icon: Bot },
              { label: 'Institutional Research',     icon: FileText },
              { label: 'Reading & Curation',         icon: Bookmark },
              { label: 'Team Intelligence',          icon: Users },
            ].map((use) => (
              <div
                key={use.label}
                className="rounded-lg bg-[#0F1730] border border-[#1F2A4D] hover:border-red-600/40 transition-colors p-3 flex items-center gap-2"
                data-testid={`landing-built-for-${use.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
              >
                <use.icon className="h-4 w-4 text-red-400 shrink-0" />
                <span className="text-xs text-slate-200 font-medium">{use.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-[#0A1024] scroll-mt-16" data-testid="landing-pricing-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-red-400 font-semibold mb-3">
              Simple Pricing
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-3 tracking-tight">
              Start free. Scale as you grow.
            </h2>
            <p className="text-base text-slate-400">
              Every plan starts with a <span className="text-red-400 font-semibold">7-day free trial</span> — no credit card required.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 max-w-7xl mx-auto">
            {PRICING_TIERS.map((tier) => {
              const isRecommended = tier.slug === 'pro';
              const isCustom = !tier.stripe_checkout;
              return (
                <Card
                  key={tier.slug}
                  className={`relative flex flex-col ${
                    isRecommended
                      ? 'border-2 border-red-600/60 shadow-xl shadow-red-900/20'
                      : isCustom
                        ? 'border border-purple-500/40 shadow-lg shadow-purple-900/10'
                        : 'border border-[#1F2A4D]'
                  }`}
                  data-testid={`landing-pricing-tier-${tier.slug}`}
                >
                  {isRecommended && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-red-600 hover:bg-red-700 text-white uppercase tracking-widest text-[10px] px-3 py-1">
                      Most Popular
                    </Badge>
                  )}
                  {isCustom && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-600 hover:bg-purple-700 text-white uppercase tracking-widest text-[10px] px-3 py-1">
                      Custom
                    </Badge>
                  )}
                  <CardContent className="p-6 flex-1 flex flex-col">
                    <div className="mb-4">
                      <h3 className="text-xl font-bold text-white mb-1">{tier.name}</h3>
                      <p className="text-xs text-slate-400">{tier.description}</p>
                    </div>

                    <div className="mb-4">
                      <div className="flex items-baseline flex-wrap">
                        <span className={`text-3xl font-bold ${isCustom ? 'text-purple-300' : 'text-white'}`}>{tier.price_label}</span>
                        <span className="text-slate-400 ml-1.5 text-sm">{tier.billing_cycle}</span>
                      </div>
                      {!isCustom && (
                        <p className="text-xs text-slate-500 mt-1">7-day free trial included</p>
                      )}
                      {isCustom && (
                        <p className="text-xs text-purple-300/70 mt-1">Volume pricing · annual contract</p>
                      )}
                    </div>

                    <div className="border-y border-[#1F2A4D] py-3 mb-4 space-y-1.5">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-400">AI credits</span>
                        <span className="text-white font-semibold">{formatCap(tier.ai_credit_cap)}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-400">Team seats</span>
                        <span className="text-white font-semibold">{formatSeats(tier.seats)}</span>
                      </div>
                    </div>

                    <ul className="space-y-1.5 mb-5 flex-1">
                      {tier.features.map((feat, idx) => (
                        <li
                          key={`${tier.slug}-${idx}`}
                          className="flex items-start text-xs text-slate-200"
                        >
                          <Check className="h-3.5 w-3.5 text-emerald-400 mr-2 flex-shrink-0 mt-0.5" />
                          <span>{feat}</span>
                        </li>
                      ))}
                    </ul>

                    {isCustom ? (
                      <Button
                        asChild
                        className="w-full py-2.5 text-sm bg-purple-600 hover:bg-purple-700 text-white"
                        data-testid={`landing-pricing-cta-${tier.slug}`}
                      >
                        <a href={`mailto:${tier.contact_email}?subject=Enterprise%20plan%20enquiry`}>
                          {tier.cta}
                        </a>
                      </Button>
                    ) : (
                      <Button
                        asChild
                        className={`w-full py-2.5 text-sm ${
                          isRecommended
                            ? 'bg-[#DC2626] hover:bg-[#B91C1C] text-white'
                            : 'bg-[#1F2A4D] hover:bg-[#2A3658] text-slate-100 border border-[#2A3658]'
                        }`}
                        data-testid={`landing-pricing-cta-${tier.slug}`}
                      >
                        <Link to="/register">{tier.cta}</Link>
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <p className="text-center text-xs text-slate-500 mt-8">
            Cancel anytime · No credit card required for trial · Pricing in USD
          </p>
        </div>
      </section>

      {/* FAQ Section — iter111 additive, sits between Pricing and the final CTA */}
      <section
        className="py-24 bg-[#0F1730] border-t border-[#1F2A4D]"
        data-testid="landing-faq-section"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <div className="text-xs uppercase tracking-[0.2em] text-red-400 font-semibold mb-3">
              Straight Answers
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 tracking-tight">
              Questions worth answering.
            </h2>
            <p className="text-base md:text-lg text-slate-400 leading-relaxed">
              No marketing fluff. If your question isn&apos;t here, book a demo and ask directly.
            </p>
          </div>

          <Accordion
            type="single"
            collapsible
            className="w-full space-y-2"
            defaultValue="faq-0"
          >
            {faqs.map((f, i) => (
              <AccordionItem
                key={`faq-${i}`}
                value={`faq-${i}`}
                className="border border-[#1F2A4D] rounded-md bg-[#0A1024] px-4 data-[state=open]:border-red-600/40"
                data-testid={`landing-faq-item-${i}`}
              >
                <AccordionTrigger className="text-left text-sm md:text-base text-slate-100 hover:no-underline hover:text-white py-4">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-slate-400 leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="mt-10 text-center">
            <p className="text-sm text-slate-500 mb-4">Still have questions?</p>
            <Button
              onClick={() => setShowBookDemo(true)}
              variant="outline"
              className="border-red-600/50 bg-transparent text-red-300 hover:bg-red-950/30 hover:text-red-200"
              data-testid="faq-book-demo-button"
            >
              <Calendar className="mr-2 h-4 w-4" />
              Book a Demo — talk to the team
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 bg-[#0A1024] border-t border-[#1F2A4D] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-600/10 via-transparent to-red-900/10" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 tracking-tight leading-tight">
            Ready to command your data?
          </h2>
          <p className="text-base md:text-xl text-slate-400 mb-10 max-w-2xl mx-auto">
            Join operators using dotbox to transform raw signals into execution-ready intelligence.
          </p>
          <Button
            asChild
            size="lg"
            className="bg-[#DC2626] hover:bg-[#B91C1C] text-white px-10 py-6 text-base shadow-xl shadow-red-900/40"
            data-testid="cta-get-started-button"
          >
            <Link to="/register" className="flex items-center">
              Get Started Free
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A1024] border-t border-[#1F2A4D] py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <DotboxLogo variant="dark" className="h-8" />
            <div className="flex flex-col md:flex-row items-center gap-3 md:gap-4">
              <button
                type="button"
                onClick={() => setShowSupport(true)}
                className="inline-flex items-center gap-1 text-slate-400 hover:text-rose-400 transition-colors text-sm"
                data-testid="footer-contact-support"
              >
                <HelpCircle className="h-3.5 w-3.5" />
                Contact Support
              </button>
              <span className="hidden md:inline text-slate-600">·</span>
              <p className="text-slate-500 text-sm">
                ©2026 dotbox.ai — All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>

      <ContactSupportModal
        open={showSupport}
        onOpenChange={setShowSupport}
        initialEmail=""
        emailLocked={false}
      />
      <BookDemoModal
        open={showBookDemo}
        onOpenChange={setShowBookDemo}
      />
    </div>
  );
};

export default LandingPage;
